/**
 * IndustryFilters — strip de filtros arriba de la lista de jobs.
 *
 * Filas (de izquierda a derecha):
 *   1. Status chips: Todos / Activos / Listos / Pausados / Completados
 *   2. Sort dropdown: ETA ASC (default) / Margin % / Margin ISK/h / Valor total
 *   3. Group toggle: agrupar por actividad (Manuf/Reactions/Research)
 *   4. Materials source toggle: Fuzzwork SDE (default) / ESI bonuses
 *
 * Sticky al hacer scroll — match con NextActions que vive arriba y la lista
 * larga que vive abajo. Mantiene contexto mientras scrolleás.
 *
 * Persistencia en localStorage:
 *   - "industry.filter.status" → 'all' | 'active' | 'ready' | 'paused' | 'done'
 *   - "industry.filter.sort"   → 'eta' | 'margin_pct' | 'margin_h' | 'value'
 *   - "industry.filter.group"  → 'on' | 'off'
 *
 * El materials source toggle (Fuzzwork vs ESI) NO se persiste — es una
 * decisión por sesión que cambia el cálculo del backend.
 */

import { useEffect, useState } from 'react'

export type StatusFilter = 'all' | 'active' | 'ready' | 'paused' | 'done'
export type SortKey = 'eta' | 'margin_pct' | 'margin_h' | 'value'

const STATUSES: Array<{ key: StatusFilter; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'active', label: 'Activos' },
  { key: 'ready', label: 'Listos' },
  { key: 'paused', label: 'Pausados' },
  { key: 'done', label: 'Completados' },
]

const SORTS: Array<{ key: SortKey; label: string }> = [
  { key: 'eta', label: 'Termina antes' },
  { key: 'margin_pct', label: 'Margin %' },
  { key: 'margin_h', label: 'Margin ISK/h' },
  { key: 'value', label: 'Valor total' },
]

function readLS<T extends string>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback
  const v = window.localStorage.getItem(key)
  return (v as T) ?? fallback
}

function writeLS(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // localStorage lleno o deshabilitado — OK, solo no persistimos
  }
}

export function IndustryFilters({
  status,
  sort,
  groupByActivity,
  useEsiMaterials,
  onStatusChange,
  onSortChange,
  onGroupChange,
  onSourceChange,
  totalCount,
  filteredCount,
}: {
  status: StatusFilter
  sort: SortKey
  groupByActivity: boolean
  useEsiMaterials: boolean
  onStatusChange: (s: StatusFilter) => void
  onSortChange: (s: SortKey) => void
  onGroupChange: (g: boolean) => void
  onSourceChange: (esi: boolean) => void
  totalCount: number
  filteredCount: number
}) {
  // Persist status + sort + group on change.
  useEffect(() => writeLS('industry.filter.status', status), [status])
  useEffect(() => writeLS('industry.filter.sort', sort), [sort])
  useEffect(
    () => writeLS('industry.filter.group', groupByActivity ? 'on' : 'off'),
    [groupByActivity],
  )

  return (
    <div className="sticky top-0 z-10 bg-eve-bg/95 backdrop-blur-sm border-y border-eve-border -mx-4 px-4 py-2.5 flex items-center gap-3 flex-wrap">
      {/* Status chips */}
      <div className="flex items-center gap-1.5" role="group" aria-label="Filtrar por status">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => onStatusChange(s.key)}
            className={`text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full border transition-colors ${
              status === s.key
                ? 'bg-eve-accent/15 border-eve-accent/50 text-eve-accent'
                : 'bg-eve-panel/40 border-eve-border/60 text-eve-muted hover:text-eve-text hover:border-eve-border'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="h-4 w-px bg-eve-border/60" aria-hidden="true" />

      {/* Sort */}
      <label className="flex items-center gap-1.5 text-[11px] text-eve-muted">
        <span className="uppercase tracking-wider font-semibold">Ordenar</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as SortKey)}
          className="bg-eve-panel border border-eve-border rounded px-2 py-1 text-eve-text text-[11px] focus:outline-none focus:border-eve-accent"
        >
          {SORTS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
      </label>

      <div className="h-4 w-px bg-eve-border/60" aria-hidden="true" />

      {/* Group toggle */}
      <label className="flex items-center gap-1.5 text-[11px] text-eve-muted cursor-pointer">
        <input
          type="checkbox"
          checked={groupByActivity}
          onChange={(e) => onGroupChange(e.target.checked)}
          className="accent-eve-accent"
        />
        <span>Agrupar por actividad</span>
      </label>

      <div className="ml-auto flex items-center gap-2">
        {/* Counter */}
        <span className="text-[10px] text-eve-muted-2 font-mono">
          {filteredCount === totalCount
            ? `${totalCount} ${totalCount === 1 ? 'job' : 'jobs'}`
            : `${filteredCount} de ${totalCount}`}
        </span>

        {/* Materials source */}
        <button
          onClick={() => onSourceChange(!useEsiMaterials)}
          className={`text-[11px] px-2.5 py-1 rounded border transition-colors ${
            useEsiMaterials
              ? 'bg-eve-info/10 border-eve-info/50 text-eve-info'
              : 'bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text'
          }`}
          title={
            useEsiMaterials
              ? 'Usando ESI per-job materials (skills + structure + rig bonuses aplicados). Solo top 5 jobs activos.'
              : 'Usando Fuzzwork SDE sin bonuses. Más rápido, sirve como baseline.'
          }
        >
          {useEsiMaterials ? 'ESI bonuses' : 'Fuzzwork SDE'}
        </button>
      </div>
    </div>
  )
}

/** Hook que lee los filtros de localStorage al mount. Llamar una vez en el
 *  page que los usa. Default si no hay nada persistido: all / eta / off. */
export function usePersistedFilters() {
  const [status, setStatus] = useState<StatusFilter>(() =>
    readLS<StatusFilter>('industry.filter.status', 'all'),
  )
  const [sort, setSort] = useState<SortKey>(() =>
    readLS<SortKey>('industry.filter.sort', 'eta'),
  )
  const [groupByActivity, setGroupByActivity] = useState<boolean>(() =>
    readLS<string>('industry.filter.group', 'off') === 'on',
  )
  return {
    status,
    sort,
    groupByActivity,
    setStatus,
    setSort,
    setGroupByActivity,
  }
}
